import { useMutation } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { useSelector } from "react-redux"
import MapView from "./MapView"
import selectors from "../../modules/selectors/datePickerSelectors"
import { fetchCountyLevel, fetchShowrooms, fetchWonOpps, fetchZipcodeLevel, getPivotRange } from "./services"
import SelectDropdown from "../../components/SelectDropdown"
import { colorPalette } from "../../data/colorPaletteForRange"
import { pivot } from "../../data/mapsData"
import { formatLongNumber } from "../../utils/formatUtils"
import CheckboxMui from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DownloadButton from "../../components/DownloadReport"
import { ShimmerComponent } from "../../components/Shimmer/ShimmerComponent"
import BouncingLogo from "../../components/Shimmer/BouncingLogo"

const Map = () => {
  const { calendarDt } = useSelector(selectors?.getCalendarYear);
  const { userData } = useSelector((state) => state?.auth);
  const currentYear = userData?.data?.current_year || calendarDt?.calendarYear?.currYear;
  const [showrooms, setShowrooms] = useState([])
  const [showShowrooms, setShowShowrooms] = useState(false)
  const [showWonOpps, setShowWonOpps] = useState(false);
  const [countyLevel, setCountyLevel] = useState([])
  const [wonOpps, setWonOpps] = useState([]);
  const [zipcodeLevel, setZipcodeLevel] = useState([]);
  const [view, setView] = useState('Zip Code')
  const [resetDropdown, setResetDropdown] = useState(false)
  const [rangeValuesArray, setRangeValuesArray] = useState([])
  const [pivotRange, setPivotRange] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  let yearsArray = [];
  const arrayMappingLength = calendarDt?.calendarYear?.currYear > userData?.data?.current_year ? calendarDt?.processedDatesLength - 1 : calendarDt?.processedDatesLength;
  for (let i = 0; i < arrayMappingLength; i++) {
    yearsArray = [...yearsArray, currentYear - i]
  }
  const years = yearsArray?.map(year => ({ label: year, value: year })) || [];
  const [year, setYear] = useState(currentYear);
  const [pivotValue, setPivotValue] = useState(pivot[0].value)
  const [prevYear, setPrevYear] = useState(null);

  useEffect(() => {
    if (years.length) {
      setYear(years[0]?.value);
    }
  }, [calendarDt]);

  const {
    data: showroomsData,
    mutate: showroomsDataMutation,
    isSuccess: isSuccessShowroomsData,
    isLoading: isLoadingShowroomsData
  } = useMutation({
    mutationFn: fetchShowrooms
  })

  const {
    data: countyLevelData,
    mutate: countyLevelDataMutation,
    isSuccess: isSuccessCountyLevelData,
    isLoading: isLoadingCountyLevelData
  } = useMutation({
    mutationFn: fetchCountyLevel
  })

  const {
    data: zipcodeLevelData,
    mutate: zipcodeLevelDataMutation,
    isSuccess: isSuccessZipcodeLevelData,
    isLoading: isLoadingZipcodeLevelData
  } = useMutation({
    mutationFn: fetchZipcodeLevel
  })

  const {
    data: wonOppsData,
    mutate: wonOppsMutation,
    isSuccess: isSuccessWonOppsData,
    isLoading: isLoadingWonOppsData
  } = useMutation({
    mutationFn: fetchWonOpps
  })

  const {
    data: pivotRangeData,
    mutate: pivotRangeMutation,
    isSuccess: isSuccessPivotRangeData,
    isLoading: isLoadingPivotRangeData
  } = useMutation({
    mutationFn: getPivotRange
  })

  const handleShowrooms = (showroomsArray) => {
    if (showroomsArray)
      return Array.isArray(showroomsArray) ? showroomsArray?.map(showroom => {
        let lat = parseFloat(showroom?.lat);
        let lon = parseFloat(showroom?.lon);
        return {
          name: showroom?.address,
          centre: [lat, lon]
        }
      }) : []
    return []
  }

  const configurePivotRange = (rangeValues) => {
    const rangeArray = [];

    if (rangeValues && rangeValues.length >= 2) {
      for (let i = 0; i < rangeValues.length - 1; i++) {
        rangeArray.push([rangeValues[i], rangeValues[i + 1]]);
      }
    }

    return rangeArray;
  };

  const getAreaColor = (data) => {
    if (data) {
      const getColor = (value) => {
        if (value) {

          let colorIndex;
          pivotRange?.forEach((range, index) => {
            if (value > range[0] && value <= range[1]) {
              colorIndex = index
            }
          })
          return colorIndex;
        }
      }

      const dataArray = Array.isArray(data) ? data?.map(item => {
        return { ...item, color: colorPalette?.[pivotValue]?.colorPalette[getColor(item?.[pivotValue]) ? getColor(item?.[pivotValue]) : 0]?.color }
      }) : []
      return dataArray
    }
  }

  const handleYearChange = (currentYear) => {
    setPrevYear(year)
    setYear(currentYear);
    setShowShowrooms(false)
    setShowWonOpps(false)
  };

  const handleView = () => {
    setView(prevView => prevView === "Zip Code" ? "County" : "Zip Code")
    setResetDropdown(true)
    setShowShowrooms(false)
    setShowWonOpps(false)
  }

  const handlePivotValue = (value) => {
    setPivotValue(value)
    setShowShowrooms(false)
    setShowWonOpps(false)
    setPivotRange([])
  }

  const checkPivotRange = () => {
    if (pivotValue !== "retail_status") {
      let dataType = rangeValuesArray?.every(piv => piv === 0)
      return dataType
    }
  }

  const getRange = (index, unit) => {
    if (pivotRange?.length > 2 && index >= 0 && pivotValue !== "retail_status") {
      let range1;
      let range2;
      if (unit) {
        if (unit === "$") {
          range1 = pivotRange?.[index][0] < 0 ? "-".concat(unit).concat((formatLongNumber(pivotRange?.[index][0] < 0 ? -pivotRange?.[index][0] : pivotRange?.[index][0]))) : unit?.concat(formatLongNumber(pivotRange?.[index][0]))
          range2 = pivotRange?.[index][1] < 0 ? "-".concat(unit).concat((formatLongNumber(pivotRange?.[index][1] < 0 ? -pivotRange?.[index][1] : pivotRange?.[index][1]))) : unit?.concat(formatLongNumber(pivotRange?.[index][1]))
        } else {
          range1 = formatLongNumber(pivotRange?.[index][0])?.toString()?.concat(unit)
          range2 = formatLongNumber(pivotRange?.[index][1])?.toString()?.concat(unit)
        }
      } else {
        range1 = formatLongNumber(pivotRange?.[index][0])
        range2 = formatLongNumber(pivotRange?.[index][1])
      }
      return <div style={{ fontSize: "12px", display: "flex", alignItems: "center" }}>{range1} - {range2}</div>
    }
    return <div></div>
  }

  const getRetailColor = (data) => {
    if (data) {
      const dataArray = Array.isArray(data) ? data.map(item => ({ ...item, color: item.retail_status === "Retail" ? colorPalette[pivotValue].colorPalette[0].color : item.retail_status === "Trade" ? colorPalette[pivotValue].colorPalette[1].color : "white" })) : []
      return dataArray
    }
  }

  const configureCountyData = area => {
    if (area) {
      if (area?.coordinates === null) {
        return [area];
      }
      if (area?.coordinates?.type === 'Point') {
        let array = [{ ...area, polygonCoord: [area?.coordinates?.coordinates?.[0] < 0 ? [area?.coordinates?.coordinates?.[1], area?.coordinates?.coordinates?.[0]] : area?.coordinates?.coordinates] }]
        return array;
      } else if (area?.coordinates?.type === 'Polygon' || area?.coordinates?.type === "MultiPolygon") {
        if (area?.coordinates?.coordinates) {
          const coords = Array.isArray(area?.coordinates?.coordinates) ?
            area?.coordinates?.coordinates?.map(coord => {
              if (Array.isArray(coord)) {
                return coord?.map(item => {
                  let ele = item?.[0] < 0 ? [item?.[1], item?.[0]] : item
                  return ele
                })
              } else {
                return []
              }
            }) : []
          const array = Array.isArray(coords) ? coords?.map((coord, index) => ({ ...area, polygonCoord: coord })) : []
          return array
        }
      }
    }
  }

  useEffect(() => {
    if (view === "Zip Code" && prevYear !== year) {
      setIsLoading(true);
      setZipcodeLevel([]);
      year && zipcodeLevelDataMutation(year);
    } else if (view === "County" && prevYear !== year) {
      setIsLoading(true);
      setCountyLevel([]);
      year && countyLevelDataMutation(year);
    }
  }, [year, view]);

  useEffect(() => {
    showroomsDataMutation();
    year && wonOppsMutation(year);
  }, [year])

  useEffect(() => {
    if (pivotValue === "retail_status") {
      // TODO: check recursively calling it not, 

      setPivotRange(["Retail", "Trade"])
    } else {
      // TODO: check recursively calling it not
      year && pivotRangeMutation({ year, pivotValue, level: view === "Zip Code" ? "zip" : "county" })
    }
  }, [year, pivotValue, view])

  useEffect(() => {
    if (isSuccessPivotRangeData) {
      const rangeValues = pivotRangeData?.data;
      const rangeArray = configurePivotRange(rangeValues)
      setRangeValuesArray(rangeValues);
      setPivotRange(rangeArray)
    }
  }, [isSuccessPivotRangeData])

  useEffect(() => {
    if (isSuccessShowroomsData) {
      let showroomsArray = showroomsData?.data
      setShowrooms(handleShowrooms(showroomsArray))
    }
  }, [isSuccessShowroomsData, showroomsData])

  useEffect(() => {
    if (isSuccessCountyLevelData) {
      let data = countyLevelData?.data;
      let dataArray = getAreaColor(data)
      let countyData = Array.isArray(dataArray) ? dataArray?.map(data => configureCountyData(data))?.flatMap(element => {
        if (Array.isArray(element)) {
          return element;
        } else {
          return [element];
        }
      }) : [];
      setCountyLevel(countyData)
    }
  }, [isSuccessCountyLevelData, countyLevelData, pivotValue, pivot, rangeValuesArray])

  useEffect(() => {
    if (isSuccessZipcodeLevelData && pivotValue) {
      let data = zipcodeLevelData?.data;
      let dataArray = pivotValue === "retail_status" ? getRetailColor(data) : getAreaColor(data)
      setZipcodeLevel(dataArray)
    }
  }, [isSuccessZipcodeLevelData, zipcodeLevelData, pivotValue, year, rangeValuesArray])

  useEffect(() => {
    if (isSuccessWonOppsData) {
      let data = wonOppsData?.data;
      setWonOpps(data);
    }
  }, [isSuccessWonOppsData, wonOppsData])

  useEffect(() => {
    if (view === "County") {
      if (showrooms?.length && countyLevel?.length && pivotValue !== "retail_status") {
        if (pivotRange?.length) {
          setIsLoading(false)
        }
      } else if (showrooms?.length && countyLevel?.length) {
        setIsLoading(false)
      }
    } else if (view === "Zip Code") {
      if (showrooms?.length && zipcodeLevel?.length && pivotValue !== "retail_status") {
        if (pivotRange?.length) {
          setIsLoading(false)
        }
      } else if (showrooms?.length && zipcodeLevel?.length) {
        setIsLoading(false)
      }
    }
  }, [showrooms, countyLevel, zipcodeLevel, pivotRange, view])

  return (
    <>
      {
        currentYear ? (
          <div style={{ padding: 10, width: "auto", height: "100%" }}>
            <div style={{ display: "flex", height: "85vh" }}>
              <MapView
                data={showrooms}
                isLoading={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading}
                showShowrooms={showShowrooms}
                countyAreas={countyLevel}
                view={view}
                zipcodeAreas={zipcodeLevel}
                wonOppsData={wonOpps}
                showWonOpps={showWonOpps}
              />
              <div style={{ padding: "0px 5px", display: "flex", flexDirection: "column", justifyContent: "space-between", backgroundColor: "white", width: "240px" }}>
                <div style={{ backgroundColor: "white", padding: "5px" }}>
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ margin: '24px 0 0 7px' }}>
                      <ToggleButtonGroup
                        disabled={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading}
                        value={"Zip Code"}
                        onChange={handleView}
                        exclusive
                        aria-label="Toggle Zip code And County View"
                        sx={{ paddingRight: '20px' }}
                      >
                        <ToggleButton
                          value={"Zip Code"}
                          style={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading ? { height: '40px', color: "#a3a3a3" } : {
                            background: view === "Zip Code" ? 'black' : '#fafafa',
                            color: view === "Zip Code" ? 'white' : 'black',
                            borderColor: 'black',
                            textTransform: 'capitalize',
                            height: '40px'
                          }}
                        >
                          Zip Code
                        </ToggleButton>
                        <ToggleButton
                          value={"County"}
                          style={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading ? { height: '40px' } : {
                            background: view === "County" ? 'black' : '#fafafa',
                            color: view === "County" ? 'white' : 'black',
                            borderColor: 'black',
                            textTransform: 'capitalize',
                            height: '40px'
                          }}
                        >
                          County
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  </div>
                  <div>
                    <SelectDropdown
                      disabled={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading}
                      width={200} options={years}
                      singleSelectDefaultValue={userData?.data?.current_year || years[0]?.value}
                      label={"Fiscal Year"}
                      handleFilterChange={handleYearChange}
                    />
                  </div>
                  <div>
                    <SelectDropdown
                      disabled={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading}
                      width={200}
                      options={view === "Zip Code" ? pivot : pivot.filter(piv => piv.value !== "retail_status")}
                      singleSelectDefaultValue={pivot[0].value}
                      label={"Pivot"}
                      handleFilterChange={handlePivotValue}
                    />
                  </div>
                  <div style={{ paddingLeft: "8px" }}>
                    <FormControlLabel
                      onChange={e => setShowWonOpps(e.target.checked)}
                      control={<CheckboxMui
                        disabled={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading}
                        defaultChecked={showWonOpps}
                        value={showWonOpps}
                        checked={showWonOpps}
                        sx={{
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                          '&.Mui-checked': {
                            color: "black",
                          },
                        }} />}
                      label={"Show Won Opps"} />
                  </div>
                  <div style={{ paddingLeft: "8px" }}>
                    <FormControlLabel
                      onChange={e => setShowShowrooms(e.target.checked)}
                      control={<CheckboxMui
                        disabled={isLoadingShowroomsData || isLoadingCountyLevelData || isLoadingWonOppsData || isLoadingZipcodeLevelData || isLoadingPivotRangeData || isLoading}
                        defaultChecked={showShowrooms}
                        value={showShowrooms}
                        checked={showShowrooms}
                        sx={{
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                          '&.Mui-checked': {
                            color: "black",
                          },
                        }} />}
                      label={"Show Showrooms"} />
                  </div>
                  {view === "Zip Code" && <Tooltip title={`Download report for ${years[0]?.value}`} placement="bottom">
                    <div style={{ paddingLeft: "8px" }} >
                      <Tooltip title={`Download report for ${years[0]?.value}`} placement="bottom">
                        <DownloadButton
                          filename="map_view_zip_data.csv"
                          setIsDownload={setIsDownload}
                          setIsDownloading={setIsDownloading}
                          isDownloading={isDownloading}
                          year={years[0]?.value}
                          isDisabled={isLoading}
                          mapDownload={true}
                          showWonOpps={showWonOpps}
                          fileType='csv'
                        />
                      </Tooltip>
                    </div>
                  </Tooltip>
                  }
                </div>
                <div style={{ backgroundColor: "white", padding: "5px" }}>
                  {pivotValue && <h4 style={{ fontSize: "1rem", fontWeight: 400, lineHeight: 1.5, margin: "3px 0" }}>
                    {pivot.find(piv => piv.value === pivotValue)?.label}
                  </h4>}
                  <div style={{ marginBottom: "25px" }}>
                    {isLoadingPivotRangeData ?
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={35} thickness={3.7} disableShrink />
                      </Box> :
                      <>
                        {
                          pivotValue && pivotValue === "retail_status" &&
                          colorPalette[pivotValue].colorPalette.map((color, index) =>
                            <div style={{ marginBottom: "3px", width: "auto", display: "flex", gap: "10px" }}>
                              <div style={{ background: color.color, height: "15px", width: "15px", border: "0.5px solid black" }}></div>
                              <div>
                                {pivotRange[index]}
                              </div>
                            </div>
                          )
                        }

                        {pivotValue && pivotValue !== "retail_status" && !checkPivotRange() && colorPalette[pivotValue].colorPalette.map((color, index) =>
                          <div style={{ marginBottom: "3px", width: "auto", display: "flex", gap: "10px" }}>
                            <div style={{ background: color.color, height: "15px", width: "15px", border: "0.5px solid black" }}></div>
                            {getRange(index, pivot.find(piv => piv.value === pivotValue)?.unit)}
                          </div>
                        )
                        }
                        {pivotValue && pivotValue !== "retail_status" && checkPivotRange() &&
                          <div style={{ marginBottom: "3px", width: "auto", display: "flex", gap: "10px" }}>
                            <div style={{ background: "white", height: "15px", width: "15px", border: "0.5px solid black" }}></div>
                            <div style={{ fontSize: "12px", display: "flex", alignItems: "center" }}>0.00 - 0.00</div>
                          </div>
                        }
                      </>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <ShimmerComponent updatedStyle={{ width: '90%', height: '100%', backgroundSize: '2000px 800px', border: '0px', margin: "auto", padding: "0 20px"}} >
              <BouncingLogo height='72vh' text='Loading...' />
          </ShimmerComponent>
        )
      }
    </>
  )
}

export default Map
